// src/pages/roles/ViewRoles.tsx
import { useMemo, useState, useEffect } from "react";
import AdminLayout from "@/layouts/AdminLayout";
import { FiPlus } from "react-icons/fi";

import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import Modal from "@/components/shared/ui/Modal";
import ConfirmDialog from "@/components/shared/ui/ConfirmDialog";
import Input from "@/components/shared/ui/Input";

import RolesTable, {
  type RoleTableFilters,
  type RoleRow,
} from "@/components/roles/RolesTable";
import CreateRole, { type UpsertRole } from "@/components/roles/forms/CreateRole";
import UpdateRole from "@/components/roles/forms/UpdateRole";

import {
  useRoles,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
} from "@/api/features/roles";
import type { Role as DtoRole } from "@/api/features/roles";

/* ------------------------------ Component --------------------------- */
export default function ViewRoles() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState<null | DtoRole>(null);

  // Confirm dialog (delete)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("Please confirm");
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmCta, setConfirmCta] = useState("Confirm");
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});

  // Errors for mutations
  const [createError, setCreateError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Filters + global search
  const [filters, setFilters] = useState<RoleTableFilters>({});
  const [globalQ, setGlobalQ] = useState("");

  // Pagination (server-driven)
  const [page, setPage] = useState(1);
  const pageSize = 10;
  useEffect(() => setPage(1), [filters, globalQ]); // cancel won't touch filters/globalQ, so page stays

  // Build API query (name/code search)
  const q = useMemo(() => {
    const bits = [globalQ, filters.name].filter(Boolean);
    return bits.length ? bits.join(" ").trim() : undefined;
  }, [globalQ, filters.name]);

  const { data, isLoading, isError, error, refetch } = useRoles({
    page,
    pageSize,
    q,
    // ⬆️ No status filter: backend only returns active/non-deleted roles.
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Map API items → table rows (server already paginates)
  // NOTE: No status / audit fields in UI now.
  const pageRows: RoleRow[] = (data?.items ?? []).map((r) => ({
    id: r.id,
    name: r.name,
    isSystemRole: r.isSystemRole,
    // If RolesTable still expects extra fields (like status/updatedAt),
    // you'll adjust that component + RoleRow later.
  }));

  /* ---------------------------- Mutations ---------------------------- */
  const createMut = useCreateRole();
  const updateMut = useUpdateRole(editOpen?.id ?? 0);
  const deleteMut = useDeleteRole();

  const onCreate = async (payload: UpsertRole) => {
    setCreateError(null);
    try {
      await createMut.mutateAsync({
        name: payload.name.trim(),
        isSystemRole: payload.isSystemRole,
      });
      setCreateOpen(false); // close only; no page reset
      refetch();
    } catch (e: any) {
      setCreateError(e?.message || "Failed to create role");
    }
  };

  const onEdit = async (payload: { name: string; isSystemRole: boolean }) => {
    if (!editOpen) return;
    setUpdateError(null);
    try {
      await updateMut.mutateAsync({
        name: payload.name.trim(),
        isSystemRole: payload.isSystemRole,
      });
      setEditOpen(null); // close only; no page reset
      refetch();
    } catch (e: any) {
      setUpdateError(e?.message || "Failed to update role");
    }
  };

  const askDelete = (role: RoleRow) => {
    setDeleteError(null);
    setConfirmTitle("Delete role");
    setConfirmMsg(`Do you want to delete the role “${role.name}”? This action cannot be undone.`);
    setConfirmCta("Delete");
    setConfirmAction(() => async () => {
      try {
        await deleteMut.mutateAsync(Number(role.id));
        setConfirmOpen(false);
        refetch();
      } catch (e: any) {
        setDeleteError(e?.message || "Failed to delete role");
      }
    });
    setConfirmOpen(true);
  };

  const serialStart = (page - 1) * pageSize;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Roles</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <Input
              value={globalQ}
              onChange={(e) => setGlobalQ(e.target.value)}
              placeholder="Search roles…"
              className="pl-8 w-56"
            />
          </div>

          <button
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
            onClick={() => setCreateOpen(true)}
          >
            <FiPlus /> Add Role
          </button>
        </div>
      </div>

      {/* Error banners */}
      {isError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Failed to load roles: {(error as Error)?.message || "Unknown error"}
        </div>
      )}
      {createError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {createError}
        </div>
      )}
      {updateError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {updateError}
        </div>
      )}
      {deleteError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {deleteError}
        </div>
      )}

      {/* Table */}
      <RolesTable
        rows={[]}
        pageRows={pageRows}
        serialStart={serialStart}
        filters={filters}
        setFilters={setFilters}
        onEdit={(r) => {
          const full = (data?.items ?? []).find((x) => x.id === r.id) as DtoRole | undefined;
          if (full) setEditOpen(full);
        }}
        onDelete={askDelete}
      />

      {/* Pagination */}
      <div className="flex items-center justify-between gap-2 border border-t-0 border-gray-200 rounded-b-2xl px-4 py-3 bg-white">
        <div className="text-xs text-gray-500">
          {isLoading ? (
            <span>Loading…</span>
          ) : (
            <>
              Showing{" "}
              <span className="font-medium text-gray-700">
                {total === 0 ? 0 : serialStart + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium text-gray-700">
                {Math.min(serialStart + pageSize, total)}
              </span>{" "}
              of{" "}
              <span className="font-medium text-gray-700">{total}</span> results
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || isLoading}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Prev
          </button>
          <div className="hidden sm:flex items-center gap-1">
            {Array.from({ length: Math.min(totalPages, 6) }).map((_, idx) => {
              const n = idx + 1;
              const active = n === page;
              return (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`rounded-md px-2.5 py-1.5 text-sm ${
                    active
                      ? "bg-sky-600 text-white"
                      : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {n}
                </button>
              );
            })}
            {totalPages > 6 && (
              <span className="px-1 text-sm text-gray-500">…</span>
            )}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || isLoading}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Modals */}
      <Modal title="Create Role" open={createOpen} onClose={() => setCreateOpen(false)}>
        <CreateRole
          onSubmit={onCreate}
          onCancel={() => setCreateOpen(false)}
        />
      </Modal>

      <Modal title="Edit Role" open={!!editOpen} onClose={() => setEditOpen(null)}>
        {editOpen ? (
          <UpdateRole
            role={{
              id: editOpen.id,
              name: editOpen.name,
              isSystemRole: editOpen.isSystemRole,
            }}
            onSubmit={(updated) =>
              onEdit({
                name: updated.name,
                isSystemRole: updated.isSystemRole,
              })
            }
            onCancel={() => setEditOpen(null)}
          />
        ) : null}
      </Modal>

      {/* Confirm delete */}
      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMsg}
        confirmText={confirmCta}
        cancelText="Cancel"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmAction}
      />
    </AdminLayout>
  );
}
